import React from "react";
import { Box, Button, TextField } from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
// unused imports removed
import Header from "../../../components/Header";
import Swal from "sweetalert2";
import { generateAndExecuteMutation } from "../../../data/Axios/DynamicService";
import { setupManagementUrl } from "../../../config";
const base_url = setupManagementUrl.uri;
/**
 * IntervalForm
 *
 * This component is used to create or edit an interval. It renders a form with
 * an input for the interval and a submit button. The form is wrapped in a
 * Formik component which handles the form state and the validation of the
 * form fields.
 *
 * @param {Object} props - The props of the component.
 * @param {boolean} props.isEditing - Whether the form is being used to edit an
 * existing interval or to create a new one.
 * @param {Object} props.data - The data of the interval to be edited. This
 * prop is only used if `isEditing` is true.
 * @param {Function} props.onClose - A function that is called when the form is
 * submitted and the modal is closed.
 * @param {Function} props.refetchData - A function that is called when the form is
 * submitted and the data of the interval is updated.
 */

const IntervalForm = (props) => {
  const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
  const userId = parseInt(decodedToken.Id);

  const initialValues = {
    interval: props.data ? props.data.interval : "",
    intervalName: props.data ? props.data.intervalName : "",
  };

  const validationSchema = yup.object().shape({
    interval: yup.string().required("interval is required"),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const { intervalName, interval } = values;
      const mutationData = {
        interval,
        intervalName,
      };

      if (props.isEditing) {
        mutationData.id = props.data.id;
        mutationData.updatedBy = userId;
        mutationData.createdBy = props.data.createdBy;
      } else {
        mutationData.createdBy = userId;
      }
      const response = await generateAndExecuteMutation(
        props.isEditing ? "updateIntervals" : "createIntervals",
        mutationData,
        "intervals",
        base_url
      );
      if (response) {
        props.onClose();
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: `Interval ${
            props.isEditing ? "updated" : "created"
          } successfully.`,
        }).then(() => {
          props.refetchData(); // Close the modal after the success message
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "An error occurred while processing your request. Please try again later.",
      });
    }

    setSubmitting(false);
  };

  return (
    <Box m="20px" bgcolor="background.paper" p={3}>
      <Header
        title={props.isEditing ? "Edit interval" : "Create interval"}
        subtitle={
          props.isEditing
            ? "Edit an existing Interval"
            : "Create a new interval"
        }
      />

      <Formik
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box display="grid" gap="30px">
              <TextField
                fullWidth
                variant="filled"
                type="text"
                label="Interval Name *"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.intervalName}
                name="intervalName"
                error={touched.intervalName && !!errors.intervalName}
                helperText={touched.intervalName && errors.intervalName}
              />
              <TextField
                fullWidth
                variant="filled"
                type="number"
                label="Interval *"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.interval}
                name="interval"
                error={touched.interval && !!errors.interval}
                helperText={touched.interval && errors.interval}
              />

              <Button
                type="submit"
                color="secondary"
                variant="contained"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Submit"}
              </Button>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default IntervalForm;
